/**
 * Historical Session Browser API Client
 *
 * Typed wrappers for session endpoints.
 * Uses the shared KodeonApiClient for authenticated HTTP requests.
 *
 * Task 2605: Global session listing now uses /v1/agents/sessions (routed).
 *
 * Transcript loading: the canonical source-of-truth for session content is
 * the ADK session-events list, exposed by the platform-agents service via
 * `/v1/agents/{agentId}/sessions/{sessionId}/history`.  The older audit-event
 * trace endpoint (`/v1/sessions/{id}/trace`) was retired from this client
 * because its history-replay collapser emitted duplicate user turns — see
 * `lib/historyAdapter.ts` and its test fixture for the full discussion.
 */

import { api } from '@/lib/api'
import type {
  SessionListItem,
  SessionListResponse,
  SessionFilters,
  TranscriptChunk,
  SessionArtifactsResponse,
} from './types'
import {
  historyToTranscriptTurns,
  type SessionHistoryResponse,
} from './lib/historyAdapter'

// ── API Paths ───────────────────────────────────────────────────────────────

const PATHS = {
  /** Cross-agent session list — routed in Task 2605 */
  globalSessions: '/v1/agents/sessions',
  /** Per-agent session list — routed in Task 2605 */
  agentSessions: (agentId: string) => `/v1/agents/${agentId}/sessions`,
  /** Canonical ADK session event history (one entry per ADK Event). */
  sessionHistory: (agentId: string, sessionId: string) =>
    `/v1/agents/${agentId}/sessions/${sessionId}/history`,
  /** Session artifacts — EXISTS in routes_generated.go */
  sessionArtifacts: (agentId: string, sessionId: string) =>
    `/v1/agents/${agentId}/sessions/${sessionId}/artifacts`,
  /** Agent list for facet population */
  agents: '/v1/agents',
} as const

// ── List Sessions ───────────────────────────────────────────────────────────

export interface ListSessionsParams {
  filters?: SessionFilters
  cursor?: string
  limit?: number
}

/**
 * List sessions across all agents.
 * Calls GET /v1/agents/sessions (Task 2605).
 *
 * The backend returns snake_case keys (has_more) while the frontend
 * type uses camelCase (hasMore). We normalise here at the boundary.
 */
export async function listSessions(
  params: ListSessionsParams = {}
): Promise<SessionListResponse> {
  const queryParams: Record<string, string | number> = {}

  if (params.limit) queryParams.limit = params.limit
  if (params.cursor) queryParams.cursor = params.cursor

  const f = params.filters
  if (f) {
    if (f.q) queryParams.q = f.q
    if (f.agent_id?.length) queryParams.agent_id = f.agent_id.join(',')
    if (f.status?.length) queryParams.status = f.status.join(',')
    if (f.source?.length) queryParams.source = f.source.join(',')
    if (f.user_id) queryParams.user_id = f.user_id
    if (f.from) queryParams.from = f.from
    if (f.to) queryParams.to = f.to
    if (f.min_duration_s !== undefined) queryParams.min_duration_s = f.min_duration_s
    if (f.min_cost_usd !== undefined) queryParams.min_cost_usd = f.min_cost_usd
    // Task 3115: pass includeSubagents only when true (false is the default, omitting saves bandwidth)
    if (f.include_subagents) queryParams.includeSubagents = 'true'
  }

  // Backend returns { sessions, has_more, cursor } — normalise to camelCase.
  const raw = await api.get<Record<string, unknown>>(PATHS.globalSessions, queryParams)
  return {
    sessions: (raw.sessions ?? []) as SessionListItem[],
    cursor: (raw.cursor as string) ?? undefined,
    hasMore: (raw.hasMore ?? raw.has_more ?? false) as boolean,
  }
}

// ── Get Transcript Chunk ────────────────────────────────────────────────────

export interface GetTranscriptChunkParams {
  /**
   * Reserved for future pagination support; currently ignored because the
   * `/history` endpoint returns the full event list in a single response.
   * Kept on the type signature so callers don't need to change when
   * pagination is re-introduced.
   */
  afterTurn?: number
  /** Same as above — accepted for API compatibility, currently ignored. */
  limit?: number
  /**
   * Optional ISO 8601 timestamp used by the adapter when an individual ADK
   * event lacks its own timestamp.  Typically the session's `startedAt`.
   * If absent, the adapter falls back to the current wall-clock time.
   */
  fallbackTimestamp?: string
  /**
   * Owning user_id for the session, if known.  ADK session storage is keyed
   * on `(app_name, user_id, session_id)`, so passing user_id lets the
   * backend do a direct partition-key lookup against the ADK session table
   * instead of probing 7 candidate user_ids in series (the dominant source
   * of the ~12s "View session" stall on the Runs tab — see Tier 1 of the
   * perf fix in `services/agents/backend/internal/handlers/http/api.py`,
   * function `_resolve_session`).
   *
   * Empty / undefined values are dropped from the query string so the
   * backend falls back to its lookup-by-session_id SQL filter (Tier 2)
   * and ultimately the candidate loop.
   */
  userId?: string
}

/**
 * Resolve a single session record (including `agentId`) by sessionId alone.
 *
 * Calls `GET /v1/agents/sessions?session_id=<id>&limit=1`.  The backend
 * `session_id` filter (added alongside this client) narrows the listing to
 * at most one row, ignoring every other query param except
 * `includeSubagents` (which is also bypassed when `session_id` is set so
 * subagent sessions resolve too).
 *
 * Returns the matching `SessionListItem`, or `null` if the session doesn't
 * exist.  Callers use this when they hold only a sessionId — typically when
 * opening a transcript modal from the Runs feed — and need the owning
 * `agentId` to build the per-agent `/history`, `/artifacts`, or `/trace`
 * URLs.  Network / 4xx / 5xx failures propagate as thrown errors so the
 * caller can distinguish "not found" from "lookup failed".
 */
export async function resolveSession(
  sessionId: string,
): Promise<SessionListItem | null> {
  const raw = await api.get<Record<string, unknown>>(PATHS.globalSessions, {
    session_id: sessionId,
    limit: 1,
  })
  const sessions = (raw.sessions ?? []) as SessionListItem[]
  return sessions[0] ?? null
}

/**
 * Fetch the full transcript for a session.
 *
 * Calls `GET /v1/agents/{agentId}/sessions/{sessionId}/history` and translates
 * the canonical ADK event list into the `TranscriptTurnItem[]` shape the
 * renderer expects.  Pagination is collapsed into a single round-trip;
 * `nextAfterTurn` is always `undefined` so the store treats the chunk as
 * complete.
 */
export async function getTranscriptChunk(
  agentId: string,
  sessionId: string,
  params: GetTranscriptChunkParams = {}
): Promise<TranscriptChunk> {
  // Forward user_id as a query param when the caller knows it.  Empty
  // strings are dropped so the backend falls back to its session_id SQL
  // filter (Tier 2 fast path) rather than running a useless `WHERE user_id
  // = ''` partition lookup.
  const query: Record<string, string> | undefined =
    params.userId && params.userId.length > 0
      ? { user_id: params.userId }
      : undefined

  const response = await api.get<SessionHistoryResponse>(
    PATHS.sessionHistory(agentId, sessionId),
    query,
  )
  const events = response?.events ?? []
  const fallbackTs = params.fallbackTimestamp ?? new Date().toISOString()
  const turns = historyToTranscriptTurns(events, fallbackTs)
  return {
    turns,
    nextAfterTurn: undefined,
  }
}

// ── Get Session Artifacts ───────────────────────────────────────────────────

/**
 * Fetch artifacts for a session.
 * GET /v1/agents/{agent_id}/sessions/{session_id}/artifacts
 */
export async function getSessionArtifacts(
  agentId: string,
  sessionId: string
): Promise<SessionArtifactsResponse> {
  return api.get<SessionArtifactsResponse>(
    PATHS.sessionArtifacts(agentId, sessionId)
  )
}

// ── Get Session History (raw, for the Trace JSON tab) ───────────────────────

/**
 * Fetch the raw ADK session history (events + state).
 *
 * Used by the Trace JSON tab so the inspector shows the same canonical data
 * the transcript view consumes.  The audit-events trace endpoint is no longer
 * called from this client.
 */
export async function getSessionTrace(
  agentId: string,
  sessionId: string,
): Promise<Record<string, unknown>> {
  return api.get<Record<string, unknown>>(
    PATHS.sessionHistory(agentId, sessionId),
  )
}

// ── Get Agents ──────────────────────────────────────────────────────────────

export interface AgentOption {
  id: string
  name: string
  display_name?: string
}

export interface AgentsListResponse {
  data: AgentOption[]
  has_more: boolean
}

/**
 * Fetch agents list for the agent filter facet.
 */
export async function listAgents(): Promise<AgentsListResponse> {
  return api.get<AgentsListResponse>(PATHS.agents)
}
